import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Sparkles, ArrowRight, Upload, Eye, Music, FileText, Clock, Shield, CheckCircle, Zap } from 'lucide-react';
import SEO from '../SEO';
import '../SeoPages.css';

function VideoComplianceChecker() {
  return (
    <>
      <SEO
        title="Video Compliance Checker - Pre-Publish Content Analysis Tool"
        description="Check your videos against YouTube and TikTok guidelines before publishing. AI-powered visual scanning, transcript analysis, and music detection to keep your content compliant."
        keywords="video compliance checker, content compliance tool, YouTube compliance check, TikTok content checker, pre-publish video analysis, content moderation tool, creator tools"
        url="/features/video-compliance-checker"
      />
      <div className="seo-page">
        <div className="seo-container">
          {/* Header */}
          <header className="seo-header">
            <Link to="/" className="seo-back-link">
              <ArrowLeft size={20} />
              <span>Back to Home</span>
            </Link>
            <Link to="/" className="seo-brand">
              <div className="seo-logo-circle">
                <Sparkles className="seo-sparkles-icon" />
              </div>
              <span className="seo-brand-name">MyCleanStudio</span>
            </Link>
          </header>

          {/* Hero */}
          <div className="seo-hero">
            <h1>Video Compliance Checker: Know Before You Publish</h1>
            <p className="seo-hero-subtitle">
              AI-powered analysis that checks your videos against platform guidelines before you hit upload.
            </p>
          </div>

          {/* Content */}
          <main className="seo-content">
            <section>
              <h2>The Creator's Dilemma</h2>
              <p>
                You pour hours into creating content. Scripting, filming, editing - it's a labor of love. But one wrong move and your video gets:
              </p>
              <ul>
                <li><strong>Demonetized</strong> - Yellow dollar sign, limited ads, lost revenue</li>
                <li><strong>Age-restricted</strong> - Hidden from most viewers</li>
                <li><strong>Shadowbanned</strong> - Crushed reach on TikTok</li>
                <li><strong>Removed</strong> - All that work, gone</li>
                <li><strong>Struck</strong> - Your entire channel at risk</li>
              </ul>
              <p>
                The frustrating part? You often don't know there's a problem until <em>after</em> you publish. By then, your video's critical first hours of performance are wasted.
              </p>

              <div className="seo-highlight-box">
                <p>
                  <strong>What if you could check your content BEFORE uploading?</strong> That's exactly what MyCleanStudio does.
                </p>
              </div>
            </section>

            <section>
              <h2>How It Works</h2>
              <p>
                MyCleanStudio scans your video the same way platforms do - but before you publish, giving you the chance to fix issues first.
              </p>

              <div className="seo-feature-list">
                <div className="seo-feature-item">
                  <div className="seo-feature-icon">
                    <Upload size={24} />
                  </div>
                  <div className="seo-feature-content">
                    <h4>1. Upload Your Video</h4>
                    <p>Upload your video file directly. We support all common video formats up to several GB.</p>
                  </div>
                </div>

                <div className="seo-feature-item">
                  <div className="seo-feature-icon">
                    <Zap size={24} />
                  </div>
                  <div className="seo-feature-content">
                    <h4>2. Complete Scan</h4>
                    <p>We scan every frame for visual issues, check for copyrighted music, and analyze your content against platform guidelines.</p>
                  </div>
                </div>

                <div className="seo-feature-item">
                  <div className="seo-feature-icon">
                    <FileText size={24} />
                  </div>
                  <div className="seo-feature-content">
                    <h4>3. Detailed Report</h4>
                    <p>Get a comprehensive report showing potential issues, exact timestamps, severity levels, and specific recommendations for fixes.</p>
                  </div>
                </div>

                <div className="seo-feature-item">
                  <div className="seo-feature-icon">
                    <CheckCircle size={24} />
                  </div>
                  <div className="seo-feature-content">
                    <h4>4. Fix & Publish Confidently</h4>
                    <p>Make informed edits, then publish knowing your content is compliant. No more surprise demonetization.</p>
                  </div>
                </div>
              </div>
            </section>

            <section>
              <h2>What We Check</h2>

              <div className="seo-problem-grid">
                <div className="seo-problem-card">
                  <div className="seo-problem-card-icon purple">
                    <Eye size={20} />
                  </div>
                  <h4>Visual Content Scanning</h4>
                  <p>Frame-by-frame scanning detects nudity, violence, drugs, weapons, and other content that could get flagged.</p>
                </div>

                <div className="seo-problem-card">
                  <div className="seo-problem-card-icon green">
                    <Music size={20} />
                  </div>
                  <h4>Copyrighted Music Detection</h4>
                  <p>Audio fingerprinting identifies copyrighted songs that could trigger Content ID claims - even background music.</p>
                </div>

                <div className="seo-problem-card">
                  <div className="seo-problem-card-icon blue">
                    <FileText size={20} />
                  </div>
                  <h4>Content Analysis</h4>
                  <p>Your video is checked for language, topics, and themes that may violate guidelines or affect monetization.</p>
                </div>

                <div className="seo-problem-card">
                  <div className="seo-problem-card-icon yellow">
                    <Shield size={20} />
                  </div>
                  <h4>Platform Guidelines</h4>
                  <p>Content is checked against YouTube and TikTok community guidelines and advertiser-friendly policies.</p>
                </div>
              </div>
            </section>

            <section>
              <h2>Detailed Reports You Can Act On</h2>
              <p>
                We don't just tell you "there might be a problem." You get specific, actionable information:
              </p>

              <ul>
                <li><strong>Exact timestamps</strong> - Know precisely where issues occur</li>
                <li><strong>Severity ratings</strong> - Understand which issues are critical vs. minor</li>
                <li><strong>Violation categories</strong> - See which specific guideline might be violated</li>
                <li><strong>Recommendations</strong> - Get suggestions for how to fix each issue</li>
                <li><strong>Platform-specific guidance</strong> - Different rules for YouTube vs. TikTok</li>
                <li><strong>Visual evidence</strong> - See flagged frames from your video</li>
              </ul>

              <div className="seo-highlight-box">
                <p>
                  <strong>Example Report Finding:</strong> "Potential demonetization risk at 2:34 - Strong profanity detected. YouTube's advertiser-friendly guidelines restrict heavy profanity in the first 30 seconds. Consider bleeping or removing."
                </p>
              </div>
            </section>

            <section>
              <h2>Why Pre-Check Your Content?</h2>

              <div className="seo-feature-list">
                <div className="seo-feature-item">
                  <div className="seo-feature-icon">
                    <Clock size={24} />
                  </div>
                  <div className="seo-feature-content">
                    <h4>Save the Critical First Hours</h4>
                    <p>Videos perform best in their first 24-48 hours. Getting demonetized or restricted during this window means lost views and revenue you can never recover.</p>
                  </div>
                </div>

                <div className="seo-feature-item">
                  <div className="seo-feature-icon">
                    <Shield size={24} />
                  </div>
                  <div className="seo-feature-content">
                    <h4>Protect Your Channel</h4>
                    <p>Repeated violations lead to strikes and termination. Catching issues before upload keeps your channel in good standing.</p>
                  </div>
                </div>

                <div className="seo-feature-item">
                  <div className="seo-feature-icon">
                    <Zap size={24} />
                  </div>
                  <div className="seo-feature-content">
                    <h4>Peace of Mind</h4>
                    <p>Publish with confidence knowing your content has been checked. No more anxiously waiting to see if you'll get a yellow dollar sign.</p>
                  </div>
                </div>
              </div>
            </section>

            <section>
              <h2>Built for Creators</h2>
              <p>
                MyCleanStudio was built specifically for content creators who are tired of:
              </p>
              <ul>
                <li>Guessing what might get flagged</li>
                <li>Finding out about issues after publishing</li>
                <li>Losing revenue to surprise demonetization</li>
                <li>Wasting time on appeals and re-edits</li>
                <li>Living in fear of strikes and bans</li>
              </ul>
              <p>
                Whether you're a YouTuber, TikToker, or multi-platform creator, knowing your content is compliant before you publish changes everything.
              </p>
            </section>

            <section>
              <h2>Get Started for Free</h2>
              <p>
                Try MyCleanStudio and see how it works. Upload your video and get a complete compliance scan.
              </p>
              <ul>
                <li>No credit card required</li>
                <li>Frame-by-frame visual scanning</li>
                <li>Copyrighted music detection</li>
                <li>Support for YouTube and TikTok guidelines</li>
              </ul>
            </section>

            {/* CTA */}
            <div className="seo-cta">
              <h2>Scan Your Video Before You Publish</h2>
              <p>Don't let compliance issues ruin your video's performance.</p>
              <Link to="/dashboard/new-check" className="seo-cta-button">
                Start Free Scan
                <ArrowRight size={18} />
              </Link>
            </div>
          </main>

          {/* Footer */}
          <footer className="seo-footer">
            <div className="seo-footer-links">
              <Link to="/guides/youtube-demonetization">Demonetization Guide</Link>
              <Link to="/guides/youtube-guidelines">YouTube Guidelines</Link>
              <Link to="/guides/tiktok-guidelines">TikTok Guidelines</Link>
              <Link to="/privacy">Privacy Policy</Link>
            </div>
            <p>&copy; {new Date().getFullYear()} MyCleanStudio. All rights reserved.</p>
          </footer>
        </div>
      </div>
    </>
  );
}

export default VideoComplianceChecker;
